import { useCallback, useMemo } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'

import LayoutContainer from 'components/Common/LayoutContainer'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import Pane from 'components/Common/Pane'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import SupportTile from 'components/Common/SupportTile/SupportTile'
import CruiseVerticalUSPs from 'components/Cruises/CruiseOfferPage/content/BookWithConfidence/CruiseVerticalUSPs'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { rem } from 'polished'

import { useCruiseDeals } from 'hooks/Cruise/useCruiseDeals'
import useNavigateTo from 'hooks/useNavigateTo'
import { CRUISE_DEALS_LIST_PAGE_HERO_IMAGE_ID } from 'constants/cruise'
import CruiseHeadData from '../Common/CruiseHeadData'
import DealsTileItem from 'components/Common/DealsTile/DealsTileItem'
import CruiseDealsListSkeleton from './CruiseDealsListSkeleton'
import CruiseLineHero from '../CruiseLineLandingPage/CruiseLineHero'

import LineShipIcon from 'LuxKit/Icons/line/LineShipIcon'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'

const CRUISE_DEALS_HERO_BASE: Omit<App.CruiseLine, 'name'> = {
  id: 'cruise-deals',
  code: 'DEALS',
  lpHeroImageId: CRUISE_DEALS_LIST_PAGE_HERO_IMAGE_ID,
  lpMobileHeroImageId: CRUISE_DEALS_LIST_PAGE_HERO_IMAGE_ID,
}

const DealsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(32)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
    gap: ${rem(40)} ${rem(20)};
  }
`

function CruiseDealsListPage() {
  const intl = useIntl()
  const cruiseDealsHeroData: App.CruiseLine = useMemo(() => ({
    ...CRUISE_DEALS_HERO_BASE,
    name: intl.formatMessage({
      id: 'GGRBIO2hI2y5g0RxQjt-',
      defaultMessage: 'Set sail with our hot cruise promotions',
      description: 'Hero heading and alt text for the cruise deals landing page',
    }),
  }), [intl])
  const { deals, isLoading } = useCruiseDeals()
  const navigateTo = useNavigateTo()

  const handleViewAllOffers = useCallback(() => {
    navigateTo('/search/cruises?isSpecialOffers=true')
  }, [navigateTo])

  const heroText = useMemo(() => {
    return intl.formatMessage(
      {
        id: '0j22h7aqmMOojXSdmI-a',
        defaultMessage: 'Set sail with our hot cruise <italic>promotions</italic>',
        description: 'Hero text for cruise deals page with italic styling on promotions',
      },
      {
        italic: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
      },
    )
  }, [intl])

  if (isLoading) {
    return (
      <div>
        <CruiseHeadData />
        <Group gap={24} direction="vertical">
          <CruiseLineHero
            cruiseLine={cruiseDealsHeroData}
          >
            {heroText}
          </CruiseLineHero>

          <LayoutContainer>
            <Sheet padding="20 16 24 16" tabletPadding="32 0 24 0">
              <Heading variant="heading2">
                {intl.formatMessage({
                  id: 'sB6YEAHXP2tGBCG5L1iC',
                  defaultMessage: 'Explore cruise offers',
                  description: 'Heading for cruise deals exploration section in the cruise section',
                })}
              </Heading>
            </Sheet>
          </LayoutContainer>
        </Group>

        <CruiseDealsListSkeleton />
      </div>
    )
  }

  return (
    <div>
      <CruiseHeadData />

      <Group gap={20} tabletGap={40} direction="vertical">
        <CruiseLineHero
          cruiseLine={cruiseDealsHeroData}
        >
          {heroText}
        </CruiseLineHero>

        <div>
          <LayoutContainer>
            <Group direction="vertical" gap={20} tabletGap={40}>
              <Heading variant="heading2">
                {intl.formatMessage({
                  id: 'sB6YEAHXP2tGBCG5L1iC',
                  defaultMessage: 'Explore cruise offers',
                  description: 'Heading for cruise deals exploration section in the cruise section',
                })}
              </Heading>

              {deals.length === 0 && !isLoading && (
                <Pane padding={16}>
                  <Group direction="vertical" gap={16} horizontalAlign="center">
                    <Heading variant="heading3" colour="neutral-two">
                      {intl.formatMessage({
                        id: 'c9CqrkhZL8z6YK2ljyCr',
                        defaultMessage: 'No deals available',
                        description: 'Heading when no cruise deals are available in the cruise section',
                      })}
                    </Heading>
                    <BodyText variant="medium" colour="neutral-three">
                      {intl.formatMessage({
                        id: 'zRnI_oh7zlH5fBkIZFaT',
                        defaultMessage: 'There are currently no active cruise deals available. Please check back later.',
                        description: 'Message explaining that no cruise deals are currently available',
                      })}
                    </BodyText>
                  </Group>
                </Pane>
              )}

              {deals.length > 0 && (
                <DealsGrid>
                  {deals.map((deal) => (
                    <DealsTileItem
                      key={deal.id}
                      backgroundColour={deal.pageColour}
                      logoImageId={deal.cruiseLine.whiteImageId ?? ''}
                      shipImageId={deal.heroImageId}
                      heading={deal.heading}
                      subHeading={deal.subHeading}
                      endDate={deal.endDate}
                      slug={deal.slug}
                      variant="grid"
                    />
                  ))}
                </DealsGrid>
              )}

              <Sheet background="highlight-primary" padding="20 40 20">
                <Group
                  direction="vertical"
                  tabletDirection="horizontal"
                  gap={20}
                  tabletGap={0}
                  horizontalAlign="center"
                  tabletHorizontalAlign="space-between"
                  tabletVerticalAlign="center"
                >
                  <Group direction="vertical" gap={8} horizontalAlign="center" tabletDirection="horizontal" tabletGap={16} tabletVerticalAlign="center">
                    <CSSBreakpoint max="mobile">
                      <LineShipIcon size="L" />
                    </CSSBreakpoint>
                    <CSSBreakpoint min="tablet">
                      <LineShipIcon size="XL" />
                    </CSSBreakpoint>
                    <HeadingTextBlock variant="heading5" align="center">
                      {intl.formatMessage({
                        id: 'w2s6ZzIDj8lEE0Ecsul3',
                        defaultMessage: 'Not seeing the cruise you’re looking for?',
                        description: 'Question asking if user cannot find the cruise they want',
                      })}
                    </HeadingTextBlock>
                  </Group>

                  <div>
                    <TextButton
                      kind="secondary"
                      variant="dark"
                      size="medium"
                      startIcon={<LineSearchIcon />}
                      onClick={handleViewAllOffers}
                      fit="full-width"
                    >
                      {intl.formatMessage({
                        id: 'PbqWJSEiA0Q2HYN9Z9ah',
                        defaultMessage: 'View all cruise offers',
                        description: 'Button text to view all cruise offers in the cruise offer or booking section',
                      })}
                    </TextButton>
                  </div>
                </Group>
              </Sheet>
            </Group>
          </LayoutContainer>
        </div>
      </Group>

      <Pane padding="40 0">
        <LayoutContainer>
          <Heading colour="neutral-one" variant="heading3" align="start">
            {intl.formatMessage({
              id: 'BO1ZPzfNouTWaFhekdTQ',
              defaultMessage: 'Why you should book with us',
              description: 'Section heading for why book with us section in the cruises section',
            })}
          </Heading>
          <CruiseVerticalUSPs />
        </LayoutContainer>
      </Pane>

      <Pane padding="40 0">
        <LayoutContainer>
          <SupportTile />
        </LayoutContainer>
      </Pane>
    </div>
  )
}

export default CruiseDealsListPage
